import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSalesModal } from '../../../hooks/useSalesModal';
import { salesService } from '../../../services/sales.service';
import { sellersService, type SellerByLocation } from '../../../services/sellers.service';
import { SalesCatalogServiceHelpers } from '../../../utils/api-helpers/SalesCatalogService.helpers';
import { CustomerProfile, Location, WarehouseInventoryProduct } from '../../../types/sales.types';
import { selectCurrentUser } from '../../../redux/slices/auth.slice';
import { useCanChangeInventoryLocation } from '../../../hooks/useCanChangeInventoryLocation';
import { useOrdersPermissions } from '../../../hooks/useOrdersPermissions';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  cartHasRemoteItems,
  countRemoteFulfillmentGroups,
  isRemoteCartItem,
} from '../../../utils/sales/mixed-sale.utils';

export type SalesCatalogScope = 'selling_branch' | 'all_branches';

export const useSalesStep1 = () => {
  const currentUser = useSelector(selectCurrentUser);
  const canChangeSalesBranch = useCanChangeInventoryLocation();
  const { canMixedSell, canChangeSeller } = useOrdersPermissions();
  // Catálogo remoto = permiso de rol (venta mixta). Independiente de canSellFromOtherLocations,
  // que solo controla la sucursal de venta (destino), no el origen de las piezas.
  const canBrowseAllBranches = canMixedSell;
  const {
    state,
    setSelectedLocation,
    setSelectedSeller,
    addProduct,
    removeProduct,
    clearCart,
    nextStep,
    canProceedToStep2,
    orderSummary,
  } = useSalesModal();

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [showDropdown, setShowDropdown] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<WarehouseInventoryProduct[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [sellers, setSellers] = useState<SellerByLocation[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingSellers, setIsLoadingSellers] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [catalogScope, setCatalogScope] = useState<SalesCatalogScope>('selling_branch');

  const catalogLocationId = useMemo(() => {
    if (!state.selectedLocation) return undefined;
    if (catalogScope === 'all_branches' && canBrowseAllBranches) {
      return undefined;
    }
    return state.selectedLocation.id;
  }, [catalogScope, canBrowseAllBranches, state.selectedLocation]);

  /** Mixed-sale catalog includes branch ASSIGNED + warehouse UNASSIGNED pieces. */
  const includeWarehouseInCatalog = catalogScope === 'all_branches' && canBrowseAllBranches;

  const searchRef = useRef<HTMLDivElement>(null);
  const customerSearchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load locations
  const loadLocations = useCallback(async () => {
    setIsLoadingLocations(true);
    try {
      const response = await SalesCatalogServiceHelpers.getLocations();

      let locationsData: Location[] = [];
      if (response && Array.isArray(response.data)) {
        locationsData = response.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          address: item.address || '',
          type: item.type,
          isActive: item.status ? item.status === 'ACTIVE' : (item.isActive ?? true),
          phone: item.phone || '',
          email: item.email || '',
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
        }));
      } else if (Array.isArray(response)) {
        locationsData = response.map((item: any) => ({
          id: item.id,
          name: item.name,
          address: item.address || '',
          type: item.type,
          isActive: item.status ? item.status === 'ACTIVE' : (item.isActive ?? true),
          phone: item.phone || '',
          email: item.email || '',
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
        }));
      }

      const branchLocations = locationsData.filter(
        (location) => location.type !== 'WAREHOUSE' && location.isActive !== false,
      );

      setLocations(branchLocations);

      if (branchLocations.length > 0) {
        const assignedLocationId = currentUser?.primaryLocationId ?? null;
        const assignedLocation = assignedLocationId
          ? branchLocations.find((location) => location.id === assignedLocationId)
          : undefined;
        const currentSelectedId = state.selectedLocation?.id ?? null;
        const currentIsWarehouse = state.selectedLocation?.type === 'WAREHOUSE';

        // Locked users: keep them on their primary branch. Bypass users: never override
        // their chosen branch (admin / *.*.* / global / can-sell-elsewhere).
        if (!canChangeSalesBranch) {
          if (assignedLocation) {
            if (currentSelectedId !== assignedLocation.id) {
              setSelectedLocation(assignedLocation);
            }
          } else if (!state.selectedLocation) {
            setSelectedLocation(branchLocations[0]);
          } else if (currentIsWarehouse) {
            setSelectedLocation(branchLocations[0]);
          }
        } else if (!state.selectedLocation || currentIsWarehouse) {
          setSelectedLocation(assignedLocation ?? branchLocations[0]);
        }
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setIsLoadingLocations(false);
    }
  }, [
    canChangeSalesBranch,
    currentUser?.primaryLocationId,
    state.selectedLocation,
    setSelectedLocation,
  ]);

  // Load sellers assigned to the selling branch (only relevant when the user can change the seller)
  const sellersRequestIdRef = useRef(0);

  const loadSellers = useCallback(async () => {
    if (!canChangeSeller || state.selectedLocation == null) {
      setSellers([]);
      return;
    }
    const locationId = state.selectedLocation.id;
    const requestId = ++sellersRequestIdRef.current;
    setIsLoadingSellers(true);
    try {
      const sellersData = await sellersService.getByLocation(locationId);
      if (requestId !== sellersRequestIdRef.current) {
        return;
      }
      setSellers(sellersData);
    } catch (error) {
      console.error('Error loading sellers:', error);
      if (requestId === sellersRequestIdRef.current) {
        setSellers([]);
      }
    } finally {
      if (requestId === sellersRequestIdRef.current) {
        setIsLoadingSellers(false);
      }
    }
  }, [canChangeSeller, state.selectedLocation]);

  useEffect(() => {
    void loadSellers();
  }, [loadSellers]);

  const handleSellerChange = useCallback(
    (sellerId: string) => {
      if (currentUser == null || sellerId === currentUser.id) {
        setSelectedSeller(null);
        return;
      }
      const seller = sellers.find((s) => s.id === sellerId);
      if (seller == null) {
        setSelectedSeller(null);
        return;
      }
      setSelectedSeller({
        id: seller.id,
        firstName: seller.firstName,
        lastName: seller.lastName,
        email: seller.email,
      });
    },
    [currentUser, sellers, setSelectedSeller],
  );

  // Load available products
  const loadAvailableProducts = useCallback(async () => {
    if (!state.selectedLocation) return;

    setIsLoadingProducts(true);
    try {
      const products = await salesService.getAvailableProducts(catalogLocationId, {
        includeWarehouse: includeWarehouseInCatalog,
      });
      setAvailableProducts(products);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  }, [state.selectedLocation, catalogLocationId, includeWarehouseInCatalog]);

  // Search products
  const searchProducts = useCallback(
    async (query: string) => {
      if (!query.trim() || !state.selectedLocation) return;

      setIsLoadingProducts(true);
      try {
        const products = await salesService.searchProducts(query, catalogLocationId, {
          includeWarehouse: includeWarehouseInCatalog,
        });
        setAvailableProducts(products);
      } catch (error) {
        console.error('Error searching products:', error);
      } finally {
        setIsLoadingProducts(false);
      }
    },
    [state.selectedLocation, catalogLocationId, includeWarehouseInCatalog],
  );

  // Search customers
  const searchCustomers = useCallback(async (query: string) => {
    if (!query.trim()) return;

    setIsLoadingCustomers(true);
    try {
      const customersData = await salesService.searchCustomers(query);
      setCustomers(customersData);
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setIsLoadingCustomers(false);
    }
  }, []);

  // Debounced search: API when query >2 chars; full branch catalog otherwise.
  useEffect(() => {
    if (!state.selectedLocation) {
      return;
    }
    const trimmed = debouncedSearchQuery.trim();
    if (trimmed.length > 2) {
      void searchProducts(trimmed);
    } else {
      void loadAvailableProducts();
    }
  }, [
    debouncedSearchQuery,
    state.selectedLocation,
    catalogLocationId,
    searchProducts,
    loadAvailableProducts,
  ]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Free-text search must keep spaces; barcode decode is only for scan-dedicated fields.
    setSearchQuery(e.target.value);
  }, []);

  const handleSearchFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setAnchorEl(e.currentTarget);
    setShowDropdown(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    setTimeout(() => setShowDropdown(false), 150);
  }, []);

  const handleCustomerSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setCustomerSearchQuery(value);
      setShowCustomerDropdown(value.length > 0);

      if (value.length > 2) {
        searchCustomers(value);
      }
    },
    [searchCustomers],
  );

  // Handle product selection
  const handleProductSelect = useCallback(
    (product: WarehouseInventoryProduct) => {
      const existingProduct = state.selectedProducts.find(
        (p) => p.warehouseInventoryProduct.id === product.id,
      );

      if (existingProduct) {
        removeProduct(product.id);
      } else {
        addProduct(product);
      }

      setShowDropdown(false);
      if (searchInputRef.current != null) {
        searchInputRef.current.value = '';
      }
      setSearchQuery('');
      loadAvailableProducts();
    },
    [state.selectedProducts, removeProduct, addProduct, loadAvailableProducts],
  );

  // Handle location change
  const handleLocationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!canChangeSalesBranch) return;
      const location = locations.find((l) => l.id === Number(e.target.value));
      if (location) {
        setSelectedLocation(location);
        setSelectedSeller(null);
        clearCart();
        setCatalogScope('selling_branch');
      }
    },
    [locations, setSelectedLocation, setSelectedSeller, clearCart, canChangeSalesBranch],
  );

  const handleMixedSaleToggle = useCallback(
    (value: SalesCatalogScope) => {
      if (value === catalogScope) return;
      // Switching the catalog origin invalidates the current selection (remote
      // pieces stop being valid when narrowing to the selling branch, and the
      // whole basis changes when widening). Mirror the branch-change behaviour:
      // wipe the cart so the user rebuilds it against the new catalog. The
      // product list refreshes on its own because catalogLocationId changes.
      if (state.selectedProducts.length > 0) {
        clearCart();
      }
      setCatalogScope(value);
    },
    [catalogScope, state.selectedProducts.length, clearCart],
  );

  const isMixedCart = useMemo(
    () => cartHasRemoteItems(state.selectedProducts, state.selectedLocation?.id),
    [state.selectedProducts, state.selectedLocation?.id],
  );

  const remoteGroupCount = useMemo(
    () => countRemoteFulfillmentGroups(state.selectedProducts, state.selectedLocation?.id),
    [state.selectedProducts, state.selectedLocation?.id],
  );

  // Client-side filter on loaded rows (1–2 char prefix on full catalog; narrows API results when longer).
  const filteredProducts = useMemo(() => {
    const query = debouncedSearchQuery.trim().toLowerCase();
    const matched = !query
      ? availableProducts
      : availableProducts.filter((product) => {
          const categoryName =
            typeof product.product.category === 'string'
              ? product.product.category
              : product.product.category?.name || '';
          const skuCode =
            typeof product.product.sku === 'string'
              ? product.product.sku
              : product.product.sku?.code || '';

          return (
            product.product.name.toLowerCase().includes(query) ||
            skuCode.toLowerCase().includes(query) ||
            categoryName.toLowerCase().includes(query) ||
            product.productCodeUnique?.toLowerCase().includes(query)
          );
        });

    // Local pieces first so a remote sale is a deliberate choice, not the default.
    const sellingId = state.selectedLocation?.id;
    if (sellingId == null || catalogScope !== 'all_branches') {
      return matched;
    }
    return [...matched].sort((a, b) => {
      const aRemote = a.location?.id != null && a.location.id !== sellingId ? 1 : 0;
      const bRemote = b.location?.id != null && b.location.id !== sellingId ? 1 : 0;
      return aRemote - bRemote;
    });
  }, [debouncedSearchQuery, availableProducts, catalogScope, state.selectedLocation?.id]);

  // Calculate total items
  const totalItems = useMemo(() => state.selectedProducts.length, [state.selectedProducts]);

  // Load initial data
  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    if (state.selectedLocation) {
      loadAvailableProducts();
    }
  }, [state.selectedLocation, catalogLocationId, loadAvailableProducts]);

  useEffect(() => {
    if (!canBrowseAllBranches && catalogScope === 'all_branches') {
      setCatalogScope('selling_branch');
    }
  }, [canBrowseAllBranches, catalogScope]);

  return {
    // State
    state,
    searchQuery,
    showDropdown,
    availableProducts,
    locations,
    customers,
    sellers,
    currentUser,
    isLoadingProducts,
    isLoadingLocations,
    isLoadingCustomers,
    isLoadingSellers,
    customerSearchQuery,
    showCustomerDropdown,
    anchorEl,
    filteredProducts,
    totalItems,
    canProceedToStep2,
    orderSummary,
    catalogScope,
    canBrowseAllBranches,
    canChangeSeller,
    isMixedCart,
    remoteGroupCount,
    isRemoteCartItem: (product: (typeof state.selectedProducts)[number]) =>
      isRemoteCartItem(product, state.selectedLocation?.id),
    // Refs
    searchRef,
    customerSearchRef,
    searchInputRef,
    // Handlers
    handleSearchChange,
    handleSearchFocus,
    handleSearchBlur,
    handleCustomerSearchChange,
    handleProductSelect,
    handleLocationChange,
    handleMixedSaleToggle,
    handleSellerChange,
    removeProduct,
    nextStep,
    canChangeSalesBranch,
  };
};
